/**
 * Copyright Higharc Inc. 2019
 *
 * Design Materials - Physically based triangle mesh materials that have procedurally defined detailing.
 * @module
 */
import type { MaterialSrc } from "common/materials/materialSrc";
import { assert, assertIsNotUndefined } from "common/utils/assertOfType";
import degreeToRadian from "common/utils/degreeToRadian";
import { productLog } from "common/utils/logging";
import { fmod } from "common/utils/math";
import { isLibraryHatchMaterial } from "geom/hatchLibrary";
import type { HatchMaterialId } from "geom/hatchMaterialId";
import type { HatchMaterialCache } from "gfx/hatchMaterialLoader";
import { getCachedLibraryHatchMaterial } from "gfx/hatchMaterialLoader";
import type { WebGLRenderer, Material, Texture, PixelFormat } from "three";
import { Color, Matrix3, MeshPhysicalMaterial, NearestFilter, RedFormat } from "three";
import { designMaterialBakedColorTextureFetch } from "../designMaterialBakedTextureCache";
import THREEAssetLoader from "../THREEAssetLoader";
import { assignSharedDefines, MaterialSharedParameterGroup } from "../threejs/higharc/MaterialSharedParameterGroup";
import { SSFXRendererOutput } from "../threejs/higharc/SSFXRendererOutput";
import type { ExtendedMaterial } from "../threejs/three-extensions";
import { getMaskFromBitmap } from "./baking/getMaskFromBitmap";
import type { HeightMap } from "./baking/heightMapFromDesignMaterial";
import {
  HEIGHT_MAP_GEN_RESOLUTION_MULTIPLIER,
  heightMapFromDesignMaterial,
} from "./baking/heightMapFromDesignMaterial";
import {
  designMaterialIncludeLightProbes,
  designMaterialIncludeScreenSpaceFX,
  includeAdvancedPCFShadows,
  includeCameraFadeLayers,
  includeCommonMaterialUniforms,
  includeEntityHighlight,
  includeSsfxRendererOutput,
} from "./customizeShader";
import { designMaterialCachedBakeColorMap } from "./designMaterialBakeColorMap";
import { designMaterialBakeRepeatPeriodAndRotation, flatColorBakedMaterial } from "./designMaterialBakeColorMapUtils";
import { defaultSdfPattern } from "./designMaterialDefaults";
import { includeDesignMaterialLayers, loadLayerTextures, setLayerUniforms } from "./designMaterialLayers";
import { evaluateMainMaskShaderChunk, includeMainMaskUniforms } from "./designMaterialLayersMainMask";
import {
  getUsedLayerFeatures,
  hasSdfDistortions,
  initUVMatrixFromNormalMap,
  preprocessMaterialParams,
  sdfCompileTimeSettingsMatch,
} from "./designMaterialParams";
import type { DesignMaterialLoadOptions, MaterialParamsWithoutSdf } from "./designMaterialRuntimeConfig";
import {
  DEFAULT_DETAIL_RENDER_MODE,
  DetailRenderMode,
  MAIN_MASK_PREFIX,
  ComponentViewMode,
  defaultDesignMaterialLoadOptions,
  isComponentViewModeLayerBased,
  isComponentViewModeSingleLayer,
  LayerViewMode,
  BakeColorTextureMode,
} from "./designMaterialRuntimeConfig";
import { includeCommonDesignMaterialShaderChunks, type DesignMaterialShaderChunks } from "./designMaterialShaderChunks";
import type {
  DesignMaterialParams,
  MaskParams,
  DesignMaterialParamsPreprocessed,
  LayerParamsPreprocessed,
  SdfCommonParamsPreprocessed,
  UsedLayerFeatures,
  SdfPatternPreprocessed,
  SdfParamsPreprocessed,
  ShaderSdfPattern,
} from "./designMaterialTypes";
import { getLoadTextureFunc } from "./getLoadTextureFunc";
import { getIdMaterial } from "./idMaterial";
import type { LoadTextureFunc } from "./loadTextureFuncType";
import {
  finishDesignMaterialNewDiagnostic,
  recordDesignMaterialFullyLoadedDuration,
  recordDesignMaterialSetOptionsDuration,
  startDesignMaterialNewDiagnostic,
} from "./materialLoadDiagnostics";
import { includeApproximateTransmission } from "./shaders/approximateTransmission";
import { ensureWorldPositionInFragShader } from "./shaders/ensureWorldPositionInFragShader";
import { includeFrostedGlassTransmission } from "./shaders/frostedGlassTransmission";
import { includeLightOffsetMap } from "./shaders/lightOffsetMap";
import { lightsPhysicalFragmentWithSpecColorChunk } from "./shaders/lightsPhysicalFragmentWithSpecColor";
import { includeMetalScreenOpacity } from "./shaders/metalScreenOpacity";
import { normalFragmentBeginChunk } from "./shaders/normalFragmentBegin";
import { normalFragmentMapsChunk } from "./shaders/normalFragmentMaps";
import { normalmapParsFragmentChunk } from "./shaders/normalmapParsFragment";
import { parallaxMapDeclarations, parallaxUvOnly, preLightParallaxMap } from "./shaders/parallaxMap";
import { includeRadialFog } from "./shaders/radialFog";
import { randomizeShaderOutput } from "./shaders/randomizeShaderOutput";
import { getSDFDeclarations, preLightSdfFull, preLightSdfMask, setSdfUniforms } from "./shaders/sdf";
import { basisHelperDeclaration, dmPerturbNormalDeclaration } from "./shaders/utils";
import { safeShaderReplace } from "./shaderUtils";
import { addToAtlas } from "./TextureAtlas";
import { TEXTURE_ATLAS_TEXTURE_WIDTH } from "./textureAtlasConstants";
import { bUniform, colorUniform, fUniform, iUniform, v2Uniform } from "./uniformHelpers";

let idCounter = 0;
type OnBeforeCompileShader = Parameters<Material["onBeforeCompile"]>[0];

const HEIGHT_MAP_MAX_PIXEL_SIZE = 1024 * HEIGHT_MAP_GEN_RESOLUTION_MULTIPLIER;

// We use a placeholder normal map to force down the number of shader permutations in three.js.
const DEFAULT_NORMAL_MAP_NAME = "normalmaps/Default_normal";

interface DesignMaterialLoadContext {
  finalParams: MaterialParamsWithoutSdf;
  renderer: WebGLRenderer;
  options: DesignMaterialLoadOptions;
  useWebgl2TextureAtlas: boolean;
  hasSSFXRenderer: boolean;
  useSDFMaskOrRandom: boolean;
  useSdfSIMD: boolean;
  componentViewMode: number;
  textureCache: { [name: string]: Texture };
  hasGeneratedHeightMap: boolean;
  parallaxHeightMap: HeightMap | null;
  detailRenderMode: DetailRenderMode;
  sdfCommonParams: SdfCommonParamsPreprocessed;
  shaderSdfPatterns: [ShaderSdfPattern, ShaderSdfPattern, ShaderSdfPattern, ShaderSdfPattern];
  mainMaskTileRepeatInterval: number;
}

function designMaterialHasMetalness(params: MaterialParamsWithoutSdf): boolean {
  if (params.metalness > 0.0) {
    return true;
  }
  for (const layer of params.layers) {
    if (layer.metalnessInfluence * layer.strength > 0.0001) {
      return true;
    }
  }
  return false;
}

export const createMaterialForParameters = (_parameters): MeshPhysicalMaterial => {
  return new MeshPhysicalMaterial({});
};

function getComponentViewChunk(componentViewMode: ComponentViewMode) {
  switch (componentViewMode) {
    case ComponentViewMode.color:
      return `gl_FragColor = linearToOutputTexel(vec4(diffuseColor.rgb, 1));`;
    case ComponentViewMode.roughness:
      return `gl_FragColor = vec4( vec3( roughnessFactor ), 1 );`;
    case ComponentViewMode.metalness:
      return `gl_FragColor = vec4( vec3( metalnessFactor ), 1 );`;
    case ComponentViewMode.textureHeight:
      return `
      vec2 debug_uvFw = fwidth(vUv);
      float debug_fwForLOD = debug_uvFw.x + debug_uvFw.y;
      float debug_lodFromUV = log2(debug_fwForLOD);
      gl_FragColor = vec4( vec3( dmGetHeight(vUv, debug_lodFromUV) * 0.5 + 0.5 ), 1 );`;
    case ComponentViewMode.layerSample:
      // When we're viewing a single layer, its parameters are always stored in the first layer in the shader.
      return `gl_FragColor = vec4( vec3( dmLayerSample0 ), 1);`;
    case ComponentViewMode.rawLayerColor:
      return `gl_FragColor = linearToOutputTexel(vec4(dmColor0, 1));`;
    case ComponentViewMode.rawLayerColorInfluence:
      return `gl_FragColor = vec4( vec3( dmColorRoughMetalHeightStrength0.x ), 1 );`;
    case ComponentViewMode.rawLayerRoughness:
      return `gl_FragColor = vec4( vec3( dmColorRoughMetalHeightStrength0.y ), 1 );`;
    case ComponentViewMode.rawLayerMetalness:
      return `gl_FragColor = vec4( vec3( dmColorRoughMetalHeightStrength0.z ), 1 );`;
    case ComponentViewMode.rawLayerHeight:
      return `gl_FragColor = vec4( vec3( dmColorRoughMetalHeightStrength0.w ), 1 );`;
    case ComponentViewMode.layerMask:
      return `gl_FragColor = vec4(vec3(dmMaskSample0), 1);`;
    case ComponentViewMode.SDFMask:
      return `gl_FragColor = vec4( vec3( depthForMask * sdfDepthForMaskMultiplier ), 1 );`;
    case ComponentViewMode.RandomMask:
      return `gl_FragColor = vec4( vec3( mainRandomMask ), 1 );`;
    case ComponentViewMode.mapNormal:
      return `#if defined(USE_NORMALMAP_TANGENTSPACE) || defined(SUPPORT_HEIGHT_LAYERS)
        gl_FragColor = vec4(mapN * 0.5 + 0.5, 1);
      #else
        gl_FragColor = vec4(0, 0, 0, 1);
      #endif`;
    case ComponentViewMode.detailGeometryNormal:
      return `gl_FragColor = vec4( normal * 0.5 + 0.5, 1 );`;
    case ComponentViewMode.SDFDistanceFromSurface:
      return `float normalizedSDFDistance = (travel / sdfDepth) * 0.5 + 0.5;
      gl_FragColor = vec4(vec3(normalizedSDFDistance), 1);`;
    case ComponentViewMode.UV:
      return `gl_FragColor = vec4( vec3(vUv.xy/120.0, 1), 1 );`;
    case ComponentViewMode.UVWithParallax:
      return `gl_FragColor = vec4( vec3(uvWithParallax.xy/120.0, 1), 1 );`;
    case ComponentViewMode.edgeWearMask:
      return `gl_FragColor = vec4( vec3(edgeWearMask), 1 );`;
    case ComponentViewMode.bakedColor:
      return `
      vec3 dmBakedColor = diffuseColor.rgb;
      dmBakedColor *= detailSelfAO;
      //dmBakedColor *= detailSelfShadow * 0.5 + 0.5;
      #ifdef SUPPORT_HEIGHT_LAYERS
      // This hack makes small scale details in height visible in the baked version
      float normalBasedDarkening = 1.0 + min(mapN.y, 0.0);

      dmBakedColor *= min(dmSelfAo, normalBasedDarkening);
      #endif
      gl_FragColor = linearToOutputTexel(vec4(dmBakedColor, 1));`;
    default:
      return "";
  }
}

const applySelfShadowAO = `
#if defined(SSFX_MATERIAL_DEV) && SSFX_MATERIAL_DEV == 1
if (ssfxMaterialOutputType != ${SSFXRendererOutput.LIGHT_WITHOUT_DETAIL.toFixed(0)}) {
#endif
  reflectedLight.directDiffuse *= detailSelfShadow;
  reflectedLight.directSpecular *= detailSelfShadow;
  reflectedLight.indirectDiffuse *= detailSelfAO;
  reflectedLight.indirectSpecular *= detailSelfAO;
#if defined(SSFX_MATERIAL_DEV) && SSFX_MATERIAL_DEV == 1
}
#endif
`;

// Note that we need to use explicit LOD for sampling to avoid shader compiler crashes on Intel Windows drivers (for example version 31.0.101.5330).
// The spec also mentions that implicit derivates are undefined inside non-uniform control flow, though in this case they work on most drivers (GLSL ES 3.00 section 8.8).
const normalMappingUtilsChunk = `float dmGetHeight(vec2 uv, float lodFromUV) {
  float height = 0.0;
  // <height_entry_point>
  return height;
}

#ifdef SUPPORT_HEIGHT_LAYERS
uniform float dmHeightFilterWidth;
vec4 dmNormalAndAOFromHeight( vec2 uv ) {
  if (dmHeightFilterWidth < 0.0) {
    return vec4(0.0, 0.0, 1.0, 1.0);
  }
  vec2 ep = vec2( dmHeightFilterWidth, 0 );
  vec2 uvFw = fwidth(uv);
  float fwForLOD = uvFw.x + uvFw.y;
  float lodFromUV = log2(fwForLOD);

  float h0 = dmGetHeight(uv, lodFromUV);
  float hN = dmGetHeight(uv + ep.yx, lodFromUV);
  float hS = dmGetHeight(uv - ep.yx, lodFromUV);
  float hE = dmGetHeight(uv + ep.xy, lodFromUV);
  float hW = dmGetHeight(uv - ep.xy, lodFromUV);

  float invDoubleWidth = 0.5 / dmHeightFilterWidth;
  const float scale = 4.0;
  const float invScale = 1.0 / scale;

  vec3 n = normalize( vec3( ( hE - hW ) * invDoubleWidth, ( hN - hS ) * invDoubleWidth, invScale ) );
  float ao = (4.0*h0 - hN - hS - hE - hW) * scale * invDoubleWidth;
  return vec4( n, clamp( ao+1.0, 0.0, 1.0 ) );
}
#endif

${dmPerturbNormalDeclaration}
`;

function anySDFPatternVariesInHeight(sdf: SdfParamsPreprocessed) {
  for (const pattern of sdf.patterns) {
    if (pattern.varyHeight && pattern.varyHeightScale > 0.0) {
      return true;
    }
  }
  return false;
}

export const shadowSideModes = ["Default", "FrontSide", "BackSide", "DoubleSide"];
export const sideModes = ["FrontSide", "BackSide", "DoubleSide"];

const cachedLibraryHatchMaterials: HatchMaterialCache = {};

let designMaterialCounter = 0;

export async function designMaterialNew(
  params: DesignMaterialParams,
  renderer: WebGLRenderer,
  loadTextureUrl: LoadTextureFunc,
  hasSSFXRenderer: boolean,
  bakedColorTextureSrc: MaterialSrc | undefined, // If this is set, the baked color texture can be fetched from the server.
  options: DesignMaterialLoadOptions = defaultDesignMaterialLoadOptions,
  diagnosticMaterialKey?: string,
) {
  return designMaterialNewFromPreprocessed(
    preprocessMaterialParams(params),
    renderer,
    loadTextureUrl,
    hasSSFXRenderer,
    bakedColorTextureSrc,
    options,
    diagnosticMaterialKey,
  );
}

export async function designMaterialNewFromPreprocessed(
  params: DesignMaterialParamsPreprocessed,
  renderer: WebGLRenderer,
  loadTextureUrl: LoadTextureFunc,
  hasSSFXRenderer: boolean,
  bakedColorTextureSrc: MaterialSrc | undefined, // If this is set, the baked color texture can be fetched from the server.
  options: DesignMaterialLoadOptions = defaultDesignMaterialLoadOptions,
  diagnosticMaterialKey?: string,
): Promise<MeshPhysicalMaterial> {
  designMaterialCounter += 1;
  const id = designMaterialCounter;
  const diagnostic = startDesignMaterialNewDiagnostic(diagnosticMaterialKey);
  performance.mark("begin-design-material-" + id);
  const material = createMaterialForParameters(params);
  material.name = "designMaterial";

  material.userData.originalDefines = Object.assign({}, material.defines);
  material.userData.materialParams = params;
  material.userData.textureLoadPromises = [];
  material.userData.dmTransparent = params.transparency !== "opaque";

  if (isLibraryHatchMaterial(params.hatchMaterialId)) {
    material.userData.cachedlibraryHatchMaterial = await getCachedLibraryHatchMaterial(
      params.hatchMaterialId as HatchMaterialId,
      cachedLibraryHatchMaterials,
    );
  }

  // Alternative material that can be used to generate a material id buffer.
  // These ids are only for detecting edges between materials, there's no easy way to map them back to material data.
  ++idCounter;
  material.userData.alternativeMaterial = getIdMaterial(idCounter);

  const [usablePromise, fullyLoadedPromise] = setDesignMaterialOptions(
    material,
    renderer,
    hasSSFXRenderer,
    loadTextureUrl,
    bakedColorTextureSrc,
    options,
  );
  try {
    const setOptionsStartTimeMs = performance.now();
    await usablePromise;
    recordDesignMaterialSetOptionsDuration(diagnostic, performance.now() - setOptionsStartTimeMs);
    const fullyLoadedStartTimeMs = performance.now();
    // TODO: This is suboptimal since we have to wait for all texture loads before the material is returned, which can delay CPU work that could in principle run concurrently with loading.
    await fullyLoadedPromise;
    recordDesignMaterialFullyLoadedDuration(diagnostic, performance.now() - fullyLoadedStartTimeMs);
    finishDesignMaterialNewDiagnostic(diagnostic, "fulfilled");
  } catch (error) {
    finishDesignMaterialNewDiagnostic(diagnostic, "rejected");
    throw error;
  }

  performance.mark("end-design-material-" + id);
  performance.measure("designMaterialNew #" + id, "begin-design-material-" + id, "end-design-material-" + id);

  return material;
}

/**
 * Completely reinitialize a design material using the provided options.
 * During normal app operation this should only be called by getDesignMaterial for initialization. This can be called from outside for testing different load options, but that should not be part of normal operation
 * @returns Two promises: One that must complete before the material is usable with the new options, and another that contains async loads that can optionally be left for later.
 */
export function setDesignMaterialOptions(
  material: MeshPhysicalMaterial,
  renderer: WebGLRenderer,
  hasSSFXRenderer: boolean,
  loadTextureUrl: LoadTextureFunc,
  bakedColorTextureSrc: MaterialSrc | undefined,
  options: DesignMaterialLoadOptions,
): [Promise<void>, Promise<void>] {
  const fullyLoadedPromises: Promise<void>[] = [];

  const setOptionsPromise: Promise<void> = (async () => {
    const lossyWebPSupported = await THREEAssetLoader.setupLossyWebPSupported();
    const loadTexture = getLoadTextureFunc(loadTextureUrl, lossyWebPSupported, options.useWebgl2TextureAtlas);
    await setDesignMaterialOptionsAndCollectAsyncLoads(
      material,
      renderer,
      hasSSFXRenderer,
      loadTexture,
      loadTextureUrl,
      fullyLoadedPromises,
      bakedColorTextureSrc,
      options,
    );
  })();
  const fullyLoadedPromise = setOptionsPromise.then(async () => {
    // fullyLoadedPromises are populated asynchronously, so we wait for setOptionsPromise first before waiting on it.
    await Promise.all(fullyLoadedPromises);
  });
  return [setOptionsPromise, fullyLoadedPromise];
}

async function setDesignMaterialOptionsAndCollectAsyncLoads(
  material: MeshPhysicalMaterial,
  renderer: WebGLRenderer,
  hasSSFXRenderer: boolean,
  loadTexture: LoadTextureFunc,
  loadTextureUrl: LoadTextureFunc,
  fullyLoadedPromises: Promise<void>[],
  bakedColorTextureSrc: MaterialSrc | undefined,
  options: DesignMaterialLoadOptions,
): Promise<void> {
  const { forceDetailRenderMode } = options;
  const materialParams: DesignMaterialParamsPreprocessed = applyLayerViewModeToParams(
    material.userData.materialParams,
    options.layerViewMode,
  );

  if (
    options.bakeColorTexture !== BakeColorTextureMode.DONT_BAKE &&
    options.bakeColorTexture !== BakeColorTextureMode.REPEATING_LAYERS
  ) {
    const bakedColorTexture = await (async () => {
      switch (options.bakeColorTexture) {
        case BakeColorTextureMode.BAKE:
          return await designMaterialCachedBakeColorMap({
            params: materialParams,
            bakedColorTextureSrc,
            renderer,
            loadTextureUrl,
            hasSSFXRenderer,
            options,
            createDesignMaterial: designMaterialNewFromPreprocessed,
          });
        case BakeColorTextureMode.FETCH_FROM_AWS:
          if (bakedColorTextureSrc) {
            return await designMaterialBakedColorTextureFetch(bakedColorTextureSrc, materialParams);
          }
          // Fall back to flat color based on the parameters if we don't have a material id.
          // We want to guarantee that we'll use the simplified shader whenever fetchBakedColorTexture is true.
          return flatColorBakedMaterial(materialParams);
        case BakeColorTextureMode.FLAT_COLOR:
        default:
          return flatColorBakedMaterial(materialParams);
      }
    })();
    // We just mutate the repeat period in place instead of cloning the texture. This repeat period should be valid for all uses of the texture.
    bakedColorTexture.texture.repeat.set(1 / bakedColorTexture.worldWidth, 1 / bakedColorTexture.worldHeight);
    bakedColorTexture.texture.rotation = degreeToRadian(bakedColorTexture.rotation);
    if (materialParams.transparency === "sdfTransparent" || materialParams.transparency === "metalScreen") {
      material.alphaTest = 0.5;
    }

    material.map = bakedColorTexture.texture;
    fullyLoadedPromises.push(bakedColorTexture.loadedPromise);

    setMaterialBasicParameters(material, materialParams, true, undefined);
    if (materialParams.transparency === "frostedGlass") {
      // Hardcoded effect to make baked version of frosted glass slightly transparent.
      // We don't support the full-blown frosted glass effect for the simplified version of the material.
      material.transparent = true;
      material.opacity = 0.9;
    }
    if (bakedColorTexture.color) {
      material.color.copy(bakedColorTexture.color);
    } else {
      material.color = new Color(1.0, 1.0, 1.0);
    }
    material.userData.isBaked = true;

    material.defines = {};

    if (material.userData.originalDefines !== undefined) {
      Object.assign(material.defines, material.userData.originalDefines);
    }

    // Disable support for three.js core lightprobes as an optimization.
    material.defines["HIGHARC_DISABLE_THREEJS_CORE_LIGHT_PROBE"] = "";

    if (hasSSFXRenderer) {
      assignSharedDefines(MaterialSharedParameterGroup.instance, material);
    }

    material.onBeforeCompile = (shader, _renderer) => {
      includeCommonMaterialUniforms(shader);

      shader.fragmentShader = safeShaderReplace(
        shader.fragmentShader,
        "#include <normal_pars_fragment>",
        `#include <normal_pars_fragment>
        #include <tonemapping_pars_fragment>
        uniform bool ssfxToneMappingEnabled;`,
        "baked designMaterial fragment shader (normal_pars_fragment)",
      );

      const designMaterialShaderChunks: Pick<
        DesignMaterialShaderChunks,
        "fragmentDefinitions" | "clipping" | "aoModulation" | "lightsFragmentMaps"
      > = {
        fragmentDefinitions: [],
        clipping: [],
        aoModulation: [],
        lightsFragmentMaps: "",
      };

      if (hasSSFXRenderer) {
        includeAdvancedPCFShadows(shader);

        designMaterialIncludeScreenSpaceFX(designMaterialShaderChunks);

        designMaterialIncludeLightProbes(designMaterialShaderChunks);

        includeSsfxRendererOutput(shader);

        includeEntityHighlight(shader);
      }

      includeTonemapping(shader);

      if (options.enableLightOffsetMap) {
        includeLightOffsetMap(shader, designMaterialShaderChunks, options.offsetMapLightCount);
      }
      ensureWorldPositionInFragShader(shader);
      includeCommonDesignMaterialShaderChunks(shader, designMaterialShaderChunks);
    };

    (material as unknown as ExtendedMaterial).onProgramChange = (params: { uniforms: { [name: string]: any } }) => {
      if (hasSSFXRenderer) {
        MaterialSharedParameterGroup.instance.useSharedUniformsInMaterial(params.uniforms);
      }
    };

    material.customProgramCacheKey = function () {
      return "bakedMaterial";
    };

    material.needsUpdate = true;
    return;
  }

  let detailRenderMode = forceDetailRenderMode !== undefined ? forceDetailRenderMode : DEFAULT_DETAIL_RENDER_MODE;
  let detailRenderModeReason = forceDetailRenderMode !== undefined ? "Force detail render mode was set." : "";
  if (materialParams.sdf.common.randomTilesMode === "RandomSplit") {
    // This mode is only supported in parallax map mode
    if (detailRenderMode !== DetailRenderMode.ParallaxMap) {
      detailRenderMode = DetailRenderMode.ParallaxMap;
      detailRenderModeReason = "Random split tiles are only supported in parallax map mode.";
    }
  }
  if (materialParams.sdf.patterns.length > 4) {
    if (detailRenderMode !== DetailRenderMode.ParallaxMap) {
      detailRenderMode = DetailRenderMode.ParallaxMap;
      detailRenderModeReason = "More than 4 SDF patterns are only supported in parallax map mode.";
    }
  }
  if (hasSdfDistortions(materialParams.sdf.distortions)) {
    if (detailRenderMode !== DetailRenderMode.ParallaxMap) {
      detailRenderMode = DetailRenderMode.ParallaxMap;
      detailRenderModeReason = "SDF distortion is only supported in parallax map mode.";
    }
  }

  const usedLayerFeatures: UsedLayerFeatures = options.disableLayers
    ? { useSDFMask: false, useSDFRandom: false, hasHeightLayers: false, useSDFMaskOrRandom: false }
    : getUsedLayerFeatures(materialParams, options.disableSDF);

  const textureCache: { [name: string]: Texture } = {};

  function loadAndTrackTexture(name: string, overrideFormat: PixelFormat | undefined) {
    const [tex, texPromise] = loadTexture(name, overrideFormat);
    if (tex) {
      textureCache[name] = tex;
      fullyLoadedPromises.push(texPromise);
    }
  }

  const layerTextureFormat = RedFormat;

  function loadAndTrackTextureInAtlas(name: string) {
    const [tex, texPromise] = loadTexture(name, RedFormat);
    if (tex) {
      const atlasPromise = texPromise.then(() => addToAtlas(renderer, name, tex, options.reproduceImplicitLodIntelBug));
      fullyLoadedPromises.push(atlasPromise);
    }
    return texPromise;
  }

  let hasGeneratedHeightMap = false;
  let parallaxHeightMap: HeightMap | null = null;
  if (materialParams.heightMapEnabled && materialParams.heightMapName) {
    const [heightMapTex, heightMapPromise] = loadTexture(materialParams.heightMapName, layerTextureFormat);
    if (heightMapTex) {
      await heightMapPromise;
      const heightMapImage = heightMapTex.image as { width: number; height: number };
      assertIsNotUndefined(heightMapImage.width);
      assert(heightMapImage.width === heightMapImage.height);
      parallaxHeightMap = {
        texture: heightMapTex,
        maskTexture: null,
        pixelSize: heightMapImage.width,
        worldXSize: materialParams.heightMapScaleU,
        worldYSize: materialParams.heightMapScaleV,
        worldZDepth: materialParams.heightMapDepth,
        rotation: materialParams.heightMapRotation,
      };
      material.userData.heightMapResolution = parallaxHeightMap.pixelSize;
    }
  } else if (
    !options.disableSDF &&
    detailRenderMode === DetailRenderMode.ParallaxMap &&
    !materialParams.mainMaskTexName
  ) {
    if (!materialParams.sdf.common.maskOnly || usedLayerFeatures.useSDFMaskOrRandom) {
      hasGeneratedHeightMap = true;
      parallaxHeightMap = heightMapFromDesignMaterial(renderer, materialParams, true, false, HEIGHT_MAP_MAX_PIXEL_SIZE);
      material.userData.heightMapResolution = parallaxHeightMap !== null ? parallaxHeightMap.pixelSize : 0;
      material.userData.heightMap = parallaxHeightMap;
    }
  }

  const useWebgl2TextureAtlas = options.useWebgl2TextureAtlas;

  // Trigger texture loads here so that callers can check if all textures have loaded.
  if (useWebgl2TextureAtlas) {
    if (materialParams.normalMapEnabled && materialParams.normalMapName) {
      loadAndTrackTexture(materialParams.normalMapName, undefined);
    }
    loadAndTrackTexture(DEFAULT_NORMAL_MAP_NAME, undefined);
  }

  if (materialParams.mainMaskTexName) {
    const texResult = await getMaskFromBitmap(materialParams.mainMaskTexName, loadTexture, layerTextureFormat);
    if (texResult) {
      textureCache[MAIN_MASK_PREFIX + materialParams.mainMaskTexName] = texResult;
      const texResultImage = texResult.image as { width: number; height: number } | undefined;
      material.userData.mainMaskTexResolution = texResultImage?.width ?? 0;
    }
  }
  if (!options.disableLayers) {
    await loadLayerTextures(
      materialParams,
      useWebgl2TextureAtlas,
      loadAndTrackTextureInAtlas,
      loadAndTrackTexture,
      loadTexture,
      layerTextureFormat,
      textureCache,
    );
  }

  // Commit material options synchronously after all required async loading is complete.
  commitMaterialOptions(
    material,
    renderer,
    useWebgl2TextureAtlas,
    hasSSFXRenderer,
    textureCache,
    options,
    detailRenderMode,
    detailRenderModeReason,
    materialParams.sdf.patterns,
    hasGeneratedHeightMap,
    parallaxHeightMap,
    usedLayerFeatures,
  );
}

function applyLayerViewModeToParams(
  paramsIn: DesignMaterialParamsPreprocessed,
  layerViewMode: LayerViewMode,
): DesignMaterialParamsPreprocessed {
  if (layerViewMode !== LayerViewMode.ALL_LAYERS) {
    return {
      ...paramsIn,
      layers: paramsIn.layers.filter((layer) => layer.originalIndex === layerViewMode - 1),
    };
  }
  return paramsIn;
}

function computeFinalParams(
  paramsIn: DesignMaterialParamsPreprocessed,
  options: DesignMaterialLoadOptions,
): { finalParams: DesignMaterialParamsPreprocessed; rotation: number; mainMaskTileRepeatInterval: number } {
  const paramsWithLayerViewModeApplied = applyLayerViewModeToParams(paramsIn, options.layerViewMode);
  if (options.bakeColorTexture !== BakeColorTextureMode.REPEATING_LAYERS) {
    return { finalParams: paramsWithLayerViewModeApplied, rotation: 0, mainMaskTileRepeatInterval: -1 };
  }

  const finalParams: DesignMaterialParamsPreprocessed = structuredClone(paramsWithLayerViewModeApplied);

  const { repeatPeriodX, repeatPeriodY, rotation, mainMaskTileRepeatInterval } =
    designMaterialBakeRepeatPeriodAndRotation(finalParams);

  function fudgeLayerRepeatPeriod(layer: LayerParamsPreprocessed | MaskParams) {
    const rotationFromCardinalDirection = fmod(layer.rotate - rotation + 45, 90) - 45;
    layer.rotate -= rotationFromCardinalDirection;

    if (layer.textureName.startsWith("DV_Pack_StoneMortar") || layer.textureName.startsWith("DV_Pack_StoneStacked")) {
      // Don't fudge layers with a stone pattern mask texture
      return;
    }

    const relativeRotation = fmod(layer.rotate - rotation, 360);
    const uIsX = Math.min(Math.abs(relativeRotation), Math.abs(relativeRotation - 180)) < 45;
    const repeatPeriodU = uIsX ? repeatPeriodX : repeatPeriodY;
    const repeatPeriodV = uIsX ? repeatPeriodY : repeatPeriodX;

    layer.scaleU = repeatPeriodU / Math.max(1.0, Math.round(repeatPeriodU / layer.scaleU));
    layer.scaleV = repeatPeriodV / Math.max(1.0, Math.round(repeatPeriodV / layer.scaleV));
  }

  for (const layer of finalParams.layers) {
    fudgeLayerRepeatPeriod(layer);
  }
  for (const mask of finalParams.masks) {
    fudgeLayerRepeatPeriod(mask);
  }

  return { finalParams, rotation: rotation ?? 0, mainMaskTileRepeatInterval };
}

function commitMaterialOptions(
  material: MeshPhysicalMaterial,
  renderer: WebGLRenderer,
  useWebgl2TextureAtlas: boolean,
  hasSSFXRenderer: boolean,
  textureCache: { [name: string]: Texture },
  options: DesignMaterialLoadOptions,
  detailRenderMode: DetailRenderMode,
  detailRenderModeReason: string,
  enabledSdfPatterns: SdfPatternPreprocessed[],
  hasGeneratedHeightMap: boolean,
  parallaxHeightMap: HeightMap | null,
  usedLayerFeatures: UsedLayerFeatures,
) {
  const { finalParams, mainMaskTileRepeatInterval } = computeFinalParams(material.userData.materialParams, options);

  material.map = null; // In case we've changed the "bakeColorTexture" parameter on the material

  const componentViewMode = options.componentViewMode;

  const { hasHeightLayers, useSDFMaskOrRandom } = usedLayerFeatures;

  setMaterialBasicParameters(material, finalParams, useWebgl2TextureAtlas, textureCache);

  material.defines = {};

  if (material.userData.originalDefines !== undefined) {
    Object.assign(material.defines, material.userData.originalDefines);
  }

  if (hasSSFXRenderer) {
    assignSharedDefines(MaterialSharedParameterGroup.instance, material);
  }

  material.defines["REPEAT_LAYERS"] = options.bakeColorTexture === BakeColorTextureMode.REPEATING_LAYERS ? "" : false;

  // Disable support for three.js core lightprobes as an optimization.
  material.defines["HIGHARC_DISABLE_THREEJS_CORE_LIGHT_PROBE"] = "";

  material.defines["SUPPORT_HEIGHT_LAYERS"] = hasHeightLayers || useWebgl2TextureAtlas ? "" : false;

  material.defines["DISABLE_SSAO"] = finalParams.disableSSAO ? "" : false;

  material.defines["USE_WEBGL2_TEXTURE_ATLAS"] = useWebgl2TextureAtlas ? "" : false;
  material.defines["SDF_TRANSPARENT"] =
    finalParams.transparency === "sdfTransparent" || finalParams.transparency === "metalScreen" ? "" : false;

  material.defines["APPLY_MAPN"] = componentViewMode !== ComponentViewMode.detailGeometryNormal ? "" : false;

  material.defines["METAL_SCREEN_MATERIAL"] = finalParams.transparency === "metalScreen" ? "" : false;

  const { sdf } = finalParams;

  let useSdfSIMD = false;

  const sdfEnabled = (useSDFMaskOrRandom || !sdf.common.maskOnly) && !options.disableSDF;

  if (detailRenderMode === DetailRenderMode.ParallaxMap || parallaxHeightMap !== null) {
    material.defines["USE_RELIEF_PARALLAX"] = "";
  } else {
    if (sdfEnabled) {
      // Set all the SDF defines only if they are needed.

      if (useSDFMaskOrRandom && sdf.common.maskOnly) {
        material.defines["USE_SDF_MASK_OR_RANDOM"] = "";
      }

      useSdfSIMD = sdfCompileTimeSettingsMatch(sdf.patterns);
      material.defines["USE_SDF_SIMD"] = useSdfSIMD ? "" : false;
      if (!useSdfSIMD) {
        productLog("Not using SDF SIMD path");
        material.defines["SECOND_PATTERN"] = enabledSdfPatterns.length > 1 ? "" : false;
        material.defines["THIRD_PATTERN"] = enabledSdfPatterns.length > 2 ? "" : false;
        material.defines["FOURTH_PATTERN"] = enabledSdfPatterns.length > 3 ? "" : false;
      }

      if (anySDFPatternVariesInHeight(sdf)) {
        material.defines["SDF_REPEAT"] = options.repeatingSDF ? "" : false;
      }
    }
  }

  material.userData.detailRenderMode = detailRenderMode;
  material.userData.detailRenderModeReason = detailRenderModeReason;

  const shaderSdfPatternsCollector: ShaderSdfPattern[] = [
    ...enabledSdfPatterns.map((pattern) => ({ ...pattern, enabledForShader: true })),
  ];
  for (let i = 0; i < 4; ++i) {
    if (i < shaderSdfPatternsCollector.length) {
      processSdfSizeForShader(shaderSdfPatternsCollector[i]);
    } else {
      shaderSdfPatternsCollector.push({ ...defaultSdfPattern, enabledForShader: false });
    }
  }
  shaderSdfPatternsCollector.splice(4, shaderSdfPatternsCollector.length - 4);
  assert(shaderSdfPatternsCollector.length === 4);
  const shaderSdfPatterns: [ShaderSdfPattern, ShaderSdfPattern, ShaderSdfPattern, ShaderSdfPattern] =
    shaderSdfPatternsCollector as [ShaderSdfPattern, ShaderSdfPattern, ShaderSdfPattern, ShaderSdfPattern];

  // Context is used to pass whatever needed data to the onBeforeCompile and assignUniforms functions
  const context: DesignMaterialLoadContext = {
    finalParams,
    options,
    renderer,
    useWebgl2TextureAtlas,
    hasSSFXRenderer,
    useSDFMaskOrRandom,
    useSdfSIMD,
    componentViewMode,
    textureCache,
    hasGeneratedHeightMap,
    parallaxHeightMap,
    detailRenderMode,
    sdfCommonParams: finalParams.sdf.common,
    shaderSdfPatterns,
    mainMaskTileRepeatInterval,
  };

  // Note that material.extensions is officially only supported for ShaderMaterial, but works also for other materials.
  if ((material as any).extensions === undefined) {
    (material as any).extensions = {};
  }
  (material as any).extensions.shaderTextureLOD = true;

  material.onBeforeCompile = function (shader, renderer) {
    if (renderer !== context.renderer) {
      // The material has been originally loaded in a different renderer.
      if (context.hasGeneratedHeightMap) {
        // The height map texture exists only in GPU memory tied to the renderer, so it needs to be recreated in the new context.
        context.parallaxHeightMap = heightMapFromDesignMaterial(
          renderer,
          finalParams,
          true,
          false,
          HEIGHT_MAP_MAX_PIXEL_SIZE,
        );
        material.userData.heightMap = context.parallaxHeightMap;
      }
      context.renderer = renderer;
    }
    onBeforeCompile(shader, context);
  };

  (material as unknown as ExtendedMaterial).onProgramChange = function (params: { uniforms: { [name: string]: any } }) {
    assignUniforms(params, context);
  };

  // The custom program code needs to contain values of all of the parameters that affect shader code,
  // with the exception of defines set in material.defines. three.js decides based on the code whether to fetch the
  // shader program from cache or compile a new program.
  const disableLayers = options.disableLayers;

  const layersHeightKey: boolean[] = [];
  for (let i = 0; i < finalParams.layers.length; i++) {
    const layerParams = finalParams.layers[i];
    if (options.disableLayers) {
      layersHeightKey.push(false);
      continue;
    }
    layersHeightKey.push(layerParams.useForHeight);
  }

  const detailKey: any[] = [];

  if (detailRenderMode === DetailRenderMode.SDF) {
    detailKey.push(sdfEnabled);
    detailKey.push(sdf.common.maskOnly);
    assert(sdf.common.randomTilesMode === "None");
    if (sdfEnabled) {
      if (useSdfSIMD) {
        if (enabledSdfPatterns.length > 0) {
          detailKey.push("" + enabledSdfPatterns[0].roundSidesMode);
        } else {
          detailKey.push("" + 0);
        }
      } else {
        let prevPatternCode = "" + 1 + false;
        // Write keys for exactly 4 patterns - the shader is hardcoded for up to 4 patterns
        assert(enabledSdfPatterns.length <= 4);
        for (let i = 0; i < 4; ++i) {
          if (i < enabledSdfPatterns.length) {
            prevPatternCode = "" + enabledSdfPatterns[i].roundSidesMode + enabledSdfPatterns[i].useBevels;
            detailKey.push(prevPatternCode);
          } else {
            detailKey.push(prevPatternCode);
          }
        }
      }
    }
  }

  const customCacheKeyArray = [
    finalParams.useSpecularColor && designMaterialHasMetalness(finalParams),
    finalParams.transparency,
    finalParams.randomizeShader ? finalParams.randomizeShaderSeed : 0,
    options.layerViewMode,
    componentViewMode,
    detailRenderMode === DetailRenderMode.ParallaxMap ? options.disableSDF : false,
    disableLayers,
    useWebgl2TextureAtlas ? "" : layersHeightKey.join(";"),
    detailKey.join(","),
    options.enableLightOffsetMap,
    options.bakeColorTexture === BakeColorTextureMode.REPEATING_LAYERS,
  ];
  const customCacheKey = customCacheKeyArray.join(";");

  // TODO: We could probably generate a hash of the customCacheKey if getProgramCode becomes a bottleneck.

  material.customProgramCacheKey = function () {
    return customCacheKey;
  };

  material.needsUpdate = true;

  return;
}

function setMaterialBasicParameters(
  material: MeshPhysicalMaterial,
  finalParams: DesignMaterialParamsPreprocessed,
  useWebgl2TextureAtlas: boolean,
  textureCache: { [name: string]: Texture } | undefined, // Undefined can be used to disable normal map
) {
  material.roughness = finalParams.roughness;
  material.metalness = finalParams.metalness;
  if (material instanceof MeshPhysicalMaterial) {
    material.reflectivity = finalParams.reflectivity;
    material.clearcoat = finalParams.clearcoat;
    material.clearcoatRoughness = finalParams.clearcoatRoughness;
    material.transmission = finalParams.transmission;
  }
  material.side = finalParams.side;
  (material as any).shadowSide = finalParams.shadowSide;
  material.color = new Color(finalParams.color);
  material.transparent =
    finalParams.transparency === "approximateClearGlass" ||
    finalParams.transparency === "alphaTransparentDoNotUse" ||
    finalParams.transparency === "metalScreen" ||
    finalParams.transparency === "frostedGlass";
  material.depthWrite = !material.transparent;
  material.opacity = finalParams.opacity;
  material.normalScale.set(finalParams.normalMapStrength, finalParams.normalMapStrength);
  // We only enable normal maps in case we're using the texture atlas, since otherwise we're out of texture slots.
  if (useWebgl2TextureAtlas && textureCache !== undefined) {
    const normalMapName =
      finalParams.normalMapEnabled && finalParams.normalMapName ? finalParams.normalMapName : DEFAULT_NORMAL_MAP_NAME;
    material.normalMap = textureCache[normalMapName];
  }

  if (finalParams.useEnvMap && finalParams.envMapIntensity > 0) {
    material.envMapIntensity = finalParams.envMapIntensity;
  } else {
    material.envMapIntensity = 0;
  }
}

function assignUniforms(params: { uniforms: { [name: string]: any } }, context: DesignMaterialLoadContext) {
  const {
    finalParams,
    options,
    hasSSFXRenderer,
    useSdfSIMD,
    textureCache,
    parallaxHeightMap,
    detailRenderMode,
    sdfCommonParams,
    shaderSdfPatterns,
    renderer,
    mainMaskTileRepeatInterval,
  } = context;

  if (hasSSFXRenderer) {
    MaterialSharedParameterGroup.instance.useSharedUniformsInMaterial(params.uniforms);
  }

  const { uniforms } = params;

  // Set default camera fade uniforms to effectively disable camera fade (never discard).
  // These can be overridden by LayerSpecificUniforms when rendering in a full viewer context.
  // With these defaults: cameraFadeAlpha = length(vViewPosition) * 1000, always >> 1, so nothing discarded.
  uniforms.minCameraFadeDistance = { value: 0 };
  uniforms.invCameraFadeRange = { value: 1000 };

  let depthForMaskMultiplier = 1.0;

  if (parallaxHeightMap !== null || detailRenderMode === DetailRenderMode.ParallaxMap) {
    depthForMaskMultiplier = setParallaxMapUniforms(
      sdfCommonParams,
      parallaxHeightMap,
      uniforms,
      finalParams,
      detailRenderMode,
    );
  } else {
    depthForMaskMultiplier = setSdfUniforms(
      finalParams.transparency,
      sdfCommonParams,
      shaderSdfPatterns,
      uniforms,
      useSdfSIMD,
    );
  }

  uniforms.dmSpecColor = colorUniform(finalParams.specularColor);

  const normalUvMat = new Matrix3();
  initUVMatrixFromNormalMap(finalParams, normalUvMat);
  uniforms.dmNormalMapUvTransform = { type: "m3", value: normalUvMat };

  let mainMaskTexEnabled = false;
  // Note that we should assign all texture uniforms in the shader source even if they're not used.
  // Otherwise the renderer might assign two different types of textures to the same texture unit, which causes an invalid operation error.
  uniforms.dmMainMaskTexture = { type: "t", value: null };
  uniforms.dmMainMaskTextureInvSize = fUniform(1.0 / TEXTURE_ATLAS_TEXTURE_WIDTH);
  if (finalParams.mainMaskTexName) {
    const mainMaskTex = textureCache[MAIN_MASK_PREFIX + finalParams.mainMaskTexName];
    uniforms.dmMainMaskTexture.value = mainMaskTex ?? null;
    if (mainMaskTex) {
      mainMaskTexEnabled = true;
      // We need to use nearest filtering for this texture so that edges between different mask values don't result in a warped UV offset.
      // If we only had a couple of possible random mask values we could threshold the value in the shader instead, but there can be any number of different values.
      mainMaskTex.magFilter = NearestFilter;
      mainMaskTex.minFilter = NearestFilter;
      mainMaskTex.anisotropy = 0;
      const mainMaskImage = mainMaskTex.image as { width: number; height: number };
      uniforms.dmMainMaskTextureInvSize.value = 1.0 / mainMaskImage.width;
    }

    // This matrix is needed in case we're in SDF mode.
    // The main mask texture shares the transform parameters with the height map.
    const mainMaskUvMat = new Matrix3();
    mainMaskUvMat.setUvTransform(
      0,
      0,
      1.0 / finalParams.heightMapScaleU,
      1.0 / finalParams.heightMapScaleV,
      degreeToRadian(finalParams.heightMapRotation),
      0,
      0,
    );
    uniforms.dmMainMaskUvTransform = { type: "m3", value: mainMaskUvMat };
  } else if (detailRenderMode === DetailRenderMode.ParallaxMap) {
    uniforms.dmMainMaskTexture.value = parallaxHeightMap ? parallaxHeightMap.maskTexture : null;
    uniforms.dmMainMaskTextureInvSize.value = parallaxHeightMap ? 1.0 / parallaxHeightMap.pixelSize : 1.0 / 1024.0;
  }

  if (options.bakeColorTexture === BakeColorTextureMode.REPEATING_LAYERS) {
    uniforms.dmMainMaskTileRepeatInterval = iUniform(mainMaskTileRepeatInterval);
  }

  uniforms.dmUseTextureForMainMask = bUniform(mainMaskTexEnabled);

  setLayerUniforms(
    options,
    context.useWebgl2TextureAtlas,
    uniforms,
    renderer,
    finalParams,
    textureCache,
    depthForMaskMultiplier,
  );

  uniforms["sdfDepthForMaskMultiplier"] = fUniform(depthForMaskMultiplier);

  uniforms["edgeWearMaskParams"] = v2Uniform(
    sdfCommonParams.edgeWearStrength * 5.0,
    (1.0 - sdfCommonParams.edgeWearSpread) * 2.0,
  );
}

function setParallaxMapUniforms(
  sdfCommonParams: SdfCommonParamsPreprocessed,
  parallaxHeightMap: HeightMap | null,
  uniforms: { [name: string]: any },
  finalParams: MaterialParamsWithoutSdf,
  detailRenderMode: DetailRenderMode,
) {
  uniforms.parallaxBumpMap = { type: "t", value: parallaxHeightMap ? parallaxHeightMap.texture : null };

  uniforms.parallaxMapWorldSize = v2Uniform(
    parallaxHeightMap ? parallaxHeightMap.worldXSize : finalParams.heightMapScaleU,
    parallaxHeightMap ? parallaxHeightMap.worldYSize : finalParams.heightMapScaleV,
  );

  uniforms.parallaxMapRotation = fUniform(
    -degreeToRadian(parallaxHeightMap ? parallaxHeightMap.rotation : finalParams.heightMapRotation),
  );

  uniforms.parallaxMapInvSize = fUniform(parallaxHeightMap ? 1.0 / parallaxHeightMap.pixelSize : 1.0);

  // It's important that parallaxScale is set relative to worldYSize, not worldXSize.
  // x values of the direction vectors are scaled in the shader so that the height map can have a non-square shape.
  // We always need to set a non-zero parallaxScale so that the mask values are correct even when we don't have any height map.
  let parallaxScale = parallaxHeightMap
    ? Math.max(parallaxHeightMap.worldZDepth / parallaxHeightMap.worldYSize, 0.0001)
    : -1;
  if (
    sdfCommonParams.maskOnly &&
    !(finalParams.heightMapEnabled && finalParams.heightMapName) &&
    finalParams.transparency === "opaque"
  ) {
    parallaxScale = -1;
  }
  const parallaxMapNormalScale = parallaxScale * (parallaxHeightMap ? parallaxHeightMap.pixelSize : 0);
  uniforms.parallaxScale = fUniform(parallaxScale);
  uniforms.parallaxMapNormalScale = fUniform(parallaxMapNormalScale);

  const depthForMaskMultiplier =
    detailRenderMode === DetailRenderMode.ParallaxMap &&
    parallaxHeightMap &&
    !(finalParams.heightMapEnabled && finalParams.heightMapName)
      ? parallaxHeightMap.worldZDepth / sdfCommonParams.depth
      : 1.0;

  uniforms.useParallaxShadows = fUniform(sdfCommonParams.useShadows ? 1 : 0);
  uniforms.parallaxAoStrength = fUniform(sdfCommonParams.useAO ? Math.min(sdfCommonParams.aoStrength * 0.5, 1.0) : 0.0);
  return depthForMaskMultiplier;
}

// helper functions

function isInvalidLayerViewMode(componentViewMode: number, layerViewMode: number, layerToViewIsDisabled: boolean) {
  return (
    (isComponentViewModeLayerBased(componentViewMode) && layerToViewIsDisabled) ||
    (isComponentViewModeSingleLayer(componentViewMode) && layerViewMode === LayerViewMode.ALL_LAYERS)
  );
}

function processSdfSizeForShader(pattern: ShaderSdfPattern) {
  if (pattern.continuousX) {
    pattern.xSize = 10000;
    pattern.xDistance = 10000;
  }
  pattern.xSize = Math.min(pattern.xSize, pattern.xDistance);
  if (pattern.continuousY) {
    pattern.ySize = 10000;
    pattern.yDistance = 10000;
  }
  pattern.ySize = Math.min(pattern.ySize, pattern.yDistance);
}

function onBeforeCompile(shader: OnBeforeCompileShader, ctx: DesignMaterialLoadContext) {
  let { options } = ctx;
  const {
    finalParams,
    hasSSFXRenderer,
    useSDFMaskOrRandom,
    componentViewMode,
    detailRenderMode,
    sdfCommonParams,
    shaderSdfPatterns,
    useWebgl2TextureAtlas,
  } = ctx;

  includeCommonMaterialUniforms(shader);

  const layerToViewIsDisabled = options.layerViewMode !== LayerViewMode.ALL_LAYERS && finalParams.layers.length === 0;

  const designMaterialShaderChunks: DesignMaterialShaderChunks = {
    fragmentDefinitions: [],
    fragmentEvaluateLayers: [],
    heightWrites: [],
    fragmentPrelight: [],
    clipping: [],
    aoModulation: [],
  };

  if (hasSSFXRenderer) {
    includeAdvancedPCFShadows(shader);
    designMaterialIncludeScreenSpaceFX(designMaterialShaderChunks);
    designMaterialIncludeLightProbes(designMaterialShaderChunks);
    includeSsfxRendererOutput(shader);
    includeEntityHighlight(shader);
    includeRadialFog(designMaterialShaderChunks, shader);
  }

  includeCameraFadeLayers(designMaterialShaderChunks);

  const allLayersDisabled = finalParams.layers.length === 0;
  if (
    (finalParams.transparency === "approximateClearGlass" || finalParams.transparency === "alphaTransparentDoNotUse") &&
    finalParams.opacity < 1 &&
    allLayersDisabled &&
    sdfCommonParams.maskOnly
  ) {
    // This is glass - we'll be generating a unique shader, so let's make it as simple as possible.
    options = { ...options };
    options.disableLayers = true;
    options.disableSDF = true;
  }

  // We need to guarantee that fragment shaders have access to uvs and also add a vertex shader definition entry point.
  // Note that we also include uv varying for the normal map, this is the only texture map we use from three.js core.
  shader.vertexShader = safeShaderReplace(
    shader.vertexShader,
    "#include <uv_pars_vertex>",
    "varying vec2 vUv;",
    "designMaterial vertex shader (uv_pars_vertex)",
  );

  shader.vertexShader = safeShaderReplace(
    shader.vertexShader,
    "#include <uv_vertex>",
    "vUv = uv;",
    "designMaterial vertex shader (uv_vertex)",
  );

  shader.fragmentShader = safeShaderReplace(
    shader.fragmentShader,
    "#include <uv_pars_fragment>",
    `varying vec2 vUv;
    vec2 uvWithParallax;

    float mainRandomMask = 0.0;
    float edgeWearMask = 0.0;
    int sdfPatternIDMask; // A bitmask where one bit corresponding to the SDF pattern is set. If the SDF backplane is hit, this will be zero.

    uniform float sdfDepthForMaskMultiplier;
    `,
    "designMaterial fragment shader (uv_pars_fragment)",
  );

  let renderDetailDeclarations = "float depthForMask = 0.0;";
  if (
    (!options.disableSDF && detailRenderMode === DetailRenderMode.ParallaxMap) ||
    (finalParams.heightMapEnabled && finalParams.heightMapName)
  ) {
    renderDetailDeclarations = parallaxMapDeclarations;
  } else {
    if (!options.disableSDF && (useSDFMaskOrRandom || !sdfCommonParams.maskOnly)) {
      renderDetailDeclarations = getSDFDeclarations(shaderSdfPatterns);
    }
  }

  shader.fragmentShader = safeShaderReplace(
    shader.fragmentShader,
    "#include <normal_pars_fragment>",
    `#include <normal_pars_fragment>
    #include <tonemapping_pars_fragment>
    #if !defined(USE_TRANSMISSION)
    uniform mat4 projectionMatrix;
    #endif
    uniform mat3 normalMatrix;
    vec3 dmDetailNormal = vec3(0, 0, 1);
    ${basisHelperDeclaration}
    ${renderDetailDeclarations}
    // <frag_def_entry_point>
    ${normalMappingUtilsChunk}
    #if defined(USE_NORMALMAP_TANGENTSPACE)
    uniform mat3 dmNormalMapUvTransform;
    #endif
    `,
    "designMaterial fragment shader (normal_pars_fragment)",
  );

  if (finalParams.useSpecularColor && designMaterialHasMetalness(finalParams)) {
    designMaterialShaderChunks.fragmentDefinitions.push("uniform vec3 dmSpecColor;");
    shader.fragmentShader = safeShaderReplace(
      shader.fragmentShader,
      "#include <lights_physical_fragment>",
      lightsPhysicalFragmentWithSpecColorChunk,
      "designMaterial fragment shader (lights_physical_fragment)",
    );
  }

  if (options.enableLightOffsetMap) {
    includeLightOffsetMap(shader, designMaterialShaderChunks, options.offsetMapLightCount);
  }

  const initTbnMatrix = "mat3 dmTbn = basis(vUv, -vViewPosition, normal);"; // Tangent-bitangent-normal matrix

  // Init "vec3 tanRay" so that it contains the pixel's ray tangent in UV space. Requires tbn, projectionMatrix and vViewPosition to be set up.
  const initUVSpaceTanRay = `
  vec3 tanRay;
  if (isPerspectiveMatrix(projectionMatrix)) {
    tanRay = normalize(-vViewPosition);
  } else {
    tanRay = vec3(0, 0, -1);
  }
  tanRay = tanRay * dmTbn;`;

  const applyDetailTgtNormal =
    componentViewMode === ComponentViewMode.mapNormal
      ? ""
      : `
  #if defined(SSFX_MATERIAL_DEV) && SSFX_MATERIAL_DEV == 1
  if (ssfxMaterialOutputType != ${SSFXRendererOutput.LIGHT_WITHOUT_DETAIL.toFixed(0)}) {
  #endif
    dmDetailNormal = detailTgtNormal;
  #if defined(SSFX_MATERIAL_DEV) && SSFX_MATERIAL_DEV == 1
  }
  #endif
  `;

  let evaluateDetail = (_initUVSpaceTanRay: string, _applyDetailTgtNormal: string) => "uvWithParallax = vUv;";
  let modulateReflectedLight = "";
  if (
    (!options.disableSDF && detailRenderMode === DetailRenderMode.ParallaxMap) ||
    (finalParams.heightMapEnabled && finalParams.heightMapName)
  ) {
    if (componentViewMode === ComponentViewMode.normalMapOnly) {
      evaluateDetail = parallaxUvOnly;
    } else {
      evaluateDetail = preLightParallaxMap;
      modulateReflectedLight = applySelfShadowAO;
    }
  } else {
    if (!options.disableSDF) {
      if (sdfCommonParams.maskOnly || componentViewMode === ComponentViewMode.normalMapOnly) {
        evaluateDetail = preLightSdfMask;
      } else {
        evaluateDetail = preLightSdfFull;
        modulateReflectedLight = applySelfShadowAO;
      }
    }
  }

  let evaluateTextureMainMask = "";
  if (!options.disableLayers) {
    includeMainMaskUniforms(designMaterialShaderChunks);
    evaluateTextureMainMask = evaluateMainMaskShaderChunk(options, detailRenderMode, finalParams);
  }

  shader.fragmentShader = safeShaderReplace(
    shader.fragmentShader,
    "#include <normalmap_pars_fragment>",
    normalmapParsFragmentChunk,
    "designMaterial fragment shader (normalmap_pars_fragment)",
  );

  shader.fragmentShader = safeShaderReplace(
    shader.fragmentShader,
    "#include <normal_fragment_begin>",
    normalFragmentBeginChunk,
    "designMaterial fragment shader (normal_fragment_begin)",
  );

  shader.fragmentShader = safeShaderReplace(
    shader.fragmentShader,
    "#include <normal_fragment_maps>",
    `float detailSelfShadow = 1.0;
    float detailSelfAO = 1.0;
    ${initTbnMatrix}
    ${evaluateDetail(initUVSpaceTanRay, applyDetailTgtNormal)}

    ${evaluateTextureMainMask}
    // End of preLight
    // <frag_eval_layers_entry_point>
    ${normalFragmentMapsChunk(componentViewMode === ComponentViewMode.normalMapOnly)}`,
    "designMaterial fragment shader (normal_fragment_maps)",
  );

  // Process layers and masks
  includeDesignMaterialLayers(options, useWebgl2TextureAtlas, designMaterialShaderChunks, finalParams);

  shader.fragmentShader = safeShaderReplace(
    shader.fragmentShader,
    "// <frag_eval_layers_entry_point>",
    designMaterialShaderChunks.fragmentEvaluateLayers.join("\n"),
    "designMaterial fragment shader (frag_eval_layers_entry_point)",
  );
  shader.fragmentShader = safeShaderReplace(
    shader.fragmentShader,
    "// <height_entry_point>",
    designMaterialShaderChunks.heightWrites.join("\n"),
    "designMaterial fragment shader (height_entry_point)",
  );

  // This line of code, for some arcane reason, seems to work around a bug that made materials render as extremely dark
  // on Samsung Galaxy Tab S6, Android version 9, Build number PPR1.180610.011.T860XXU1ASL3
  designMaterialShaderChunks.fragmentPrelight.push("diffuseColor = diffuseColor + 0.001;");

  if (componentViewMode !== ComponentViewMode.normalMapOnly) {
    designMaterialShaderChunks.fragmentDefinitions.push(
      `#ifdef SUPPORT_HEIGHT_LAYERS
      uniform float layerHeightAoStrength;
      #endif`,
    );
    designMaterialShaderChunks.aoModulation.push(
      `#ifdef SUPPORT_HEIGHT_LAYERS
      float dmSelfAo = mix(1.0, dmHeightBasedNormalAndAO.w, layerHeightAoStrength);
      reflectedLight.indirectDiffuse *= dmSelfAo;
      reflectedLight.indirectSpecular *= dmSelfAo;
      #endif`,
    );
    designMaterialShaderChunks.aoModulation.push(modulateReflectedLight);
  }

  if (componentViewMode === ComponentViewMode.light || componentViewMode === ComponentViewMode.normalMapOnly) {
    designMaterialShaderChunks.fragmentPrelight.push("diffuseColor = vec4(1);");
  }

  const randomizeFinalColor = finalParams.randomizeShader ? randomizeShaderOutput(finalParams.randomizeShaderSeed) : "";

  let finalColorOverride = "";

  const sdfDistanceFromHeightMap =
    componentViewMode === ComponentViewMode.SDFDistanceFromSurface &&
    (detailRenderMode === DetailRenderMode.ParallaxMap || (finalParams.heightMapEnabled && finalParams.heightMapName));
  if (
    isInvalidLayerViewMode(componentViewMode, options.layerViewMode, layerToViewIsDisabled) ||
    sdfDistanceFromHeightMap
  ) {
    finalColorOverride = "gl_FragColor = vec4( 0.5, 0, 0.5, 1 );";
  } else if (
    componentViewMode !== ComponentViewMode.finalMaterial &&
    componentViewMode !== ComponentViewMode.light &&
    componentViewMode !== ComponentViewMode.normalMapOnly
  ) {
    finalColorOverride = getComponentViewChunk(componentViewMode);
  }

  if (finalParams.transparency === "metalScreen") {
    includeMetalScreenOpacity(shader);
  }
  if (finalParams.transparency === "approximateClearGlass") {
    includeApproximateTransmission(shader);
  }
  if (finalParams.transparency === "frostedGlass") {
    includeFrostedGlassTransmission(shader);
  }

  designMaterialShaderChunks.fragmentDefinitions.push("uniform bool ssfxToneMappingEnabled;");

  if (designMaterialShaderChunks.fog !== undefined) {
    shader.fragmentShader = safeShaderReplace(
      shader.fragmentShader,
      "#include <fog_fragment>",
      designMaterialShaderChunks.fog,
      "designMaterial fragment shader (fog_fragment)",
    );
  }

  includeCommonDesignMaterialShaderChunks(shader, designMaterialShaderChunks);

  shader.fragmentShader = safeShaderReplace(
    shader.fragmentShader,
    "#include <emissivemap_fragment>",
    `#include <emissivemap_fragment>
    ${designMaterialShaderChunks.fragmentPrelight.join("\n")}
    `,
    "designMaterial fragment shader (emissivemap_fragment)",
  );

  if (finalParams.transparency !== "approximateClearGlass") {
    includeTonemapping(shader);
  }

  shader.fragmentShader = safeShaderReplace(
    shader.fragmentShader,
    "#include <colorspace_fragment>",
    `${finalColorOverride}
    ${randomizeFinalColor}
    #include <colorspace_fragment>
    `,
    "designMaterial fragment shader (colorspace_fragment)",
  );
}

function includeTonemapping(shader: OnBeforeCompileShader) {
  // Three.js core only enables tonemapping for the final output, so we support it here using custom defines. The defines get set by MaterialSharedParameterGroup
  shader.fragmentShader = safeShaderReplace(
    shader.fragmentShader,
    "#include <tonemapping_fragment>",
    `if (ssfxToneMappingEnabled) {
      #if defined(DM_LINEAR_TONE_MAPPING) && DM_LINEAR_TONE_MAPPING == 1
        gl_FragColor.rgb = LinearToneMapping(gl_FragColor.rgb);
      #elif defined(DM_REINHARD_TONE_MAPPING) && DM_REINHARD_TONE_MAPPING == 1
        gl_FragColor.rgb = ReinhardToneMapping(gl_FragColor.rgb);
      #elif defined(DM_CINEON_TONE_MAPPING) && DM_CINEON_TONE_MAPPING == 1
        gl_FragColor.rgb = OptimizedCineonToneMapping(gl_FragColor.rgb);
      #elif defined(DM_ACES_FILMIC_TONE_MAPPING) && DM_ACES_FILMIC_TONE_MAPPING == 1
        gl_FragColor.rgb = ACESFilmicToneMapping(gl_FragColor.rgb);
      #endif
    }
    `,
    "designMaterial fragment shader (tonemapping_fragment)",
  );
}
